import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ShieldCheck, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme, AcademyThemeScope, useScopedAcademyTheme } from '../../contexts/ThemeContext';
import { apiCall } from '../../services/apiUtil';
import { setStepUpToken } from '../../services/stepUp';

/**
 * STEP-UP GATE
 * ------------
 * Mounted once at the app root. Listens for the 'stepup:required' event that
 * apiCall raises when the server answers 428, collects the emailed code, and
 * hands a step-up token back so the original request can be replayed.
 *
 * The user never re-submits anything: they see a code prompt over whatever they
 * were doing, and their action completes on its own once they verify.
 */

const PURPOSE_COPY = {
  profile_update: {
    title: 'Confirm it\'s you',
    body:  'For your security, changing your email, phone, password or security questions needs a verification code.',
  },
  access_rights: {
    title: 'Confirm it\'s you',
    body:  'Changing who has access to this academy needs a verification code.',
  },
  permanent_delete: {
    title: 'Confirm permanent deletion',
    body:  'This cannot be undone. Enter the verification code we emailed you to confirm.',
  },
};

const StepUpGate = () => {
  const scopedAcademyTheme = useScopedAcademyTheme();
  const [request, setRequest]   = useState(null);   // { purpose, settle }
  const [code, setCode]         = useState('');
  const [sending, setSending]   = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sentTo, setSentTo]     = useState('');
  const inputRef = useRef(null);

  const sendCode = useCallback(async (purpose) => {
    setSending(true);
    try {
      const res = await apiCall('/auth/step-up/request', {
        method: 'POST',
        body: JSON.stringify({ purpose }),
      });
      setSentTo(res?.sentTo || '');
      toast.success('Verification code sent to your email');
    } catch (err) {
      toast.error(err?.message || 'Could not send a verification code');
    } finally {
      setSending(false);
    }
  }, []);

  useEffect(() => {
    // Tells promptStepUp() that someone is listening — without it, a request
    // raised before this mounts would hang for ever instead of failing.
    window.__stepUpGateMounted = true;

    const onRequired = (e) => {
      const { purpose, settle } = e.detail || {};
      if (!purpose || !settle) return;
      setRequest({ purpose, settle });
      setCode('');
      setSentTo('');
      sendCode(purpose);
    };

    window.addEventListener('stepup:required', onRequired);
    return () => {
      window.__stepUpGateMounted = false;
      window.removeEventListener('stepup:required', onRequired);
    };
  }, [sendCode]);

  useEffect(() => {
    if (request) setTimeout(() => inputRef.current?.focus(), 50);
  }, [request]);

  const close = (token) => {
    request?.settle?.(token || null);
    setRequest(null);
    setCode('');
  };

  const verify = async () => {
    if (code.trim().length < 4) { toast.error('Enter the 4-digit code'); return; }
    setVerifying(true);
    try {
      const res = await apiCall('/auth/step-up/verify', {
        method: 'POST',
        body: JSON.stringify({ purpose: request.purpose, code: code.trim() }),
      });
      setStepUpToken(request.purpose, res.stepUpToken, res.expiresInMinutes);
      close(res.stepUpToken);
    } catch (err) {
      toast.error(err?.message || 'That code did not work');
      setCode('');
      inputRef.current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  if (!request) return null;
  const copy = PURPOSE_COPY[request.purpose] || PURPOSE_COPY.profile_update;

  /* ⚠️ WRAPPED IN THE ACADEMY'S SCOPE, NOT THE APP'S.
   *
   * This gate is mounted once at the app root — it has to be, since any screen
   * can demand a code — which puts it ABOVE AcademyThemeScope in the tree. So
   * it inherited the visitor's own theme and drew a blue dialog on top of an
   * orange academy, while confirming an action belonging to that academy.
   * `useScopedAcademyTheme()` is null everywhere else, and the scope is then a
   * no-op, so this costs nothing outside an academy.
   */
  return (
    <AcademyThemeScope themeKey={scopedAcademyTheme}>
      <StepUpDialog
        copy={copy}
        sending={sending}
        sentTo={sentTo}
        code={code}
        setCode={setCode}
        verifying={verifying}
        inputRef={inputRef}
        onVerify={verify}
        onResend={() => sendCode(request.purpose)}
        onClose={() => close(null)}
      />
    </AcademyThemeScope>
  );
};

/** The dialog itself, so that it reads the theme from INSIDE the scope above. */
const StepUpDialog = ({
  copy, sending, sentTo, code, setCode, verifying, inputRef, onVerify, onResend, onClose,
}) => {
  const { theme } = useTheme();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
      <div className={`${theme.card} border rounded-2xl w-full max-w-sm shadow-2xl`}>
        <div className={`flex items-center justify-between p-5 border-b ${theme.border}`}>
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} className={theme.link} />
            <h3 className={`text-lg font-bold ${theme.text}`}>{copy.title}</h3>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg ${theme.cardHover}`}>
            <X size={18} className={theme.textSecondary} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className={`text-sm ${theme.textSecondary}`}>{copy.body}</p>
          <p className={`text-sm ${theme.textSecondary}`}>
            {sending
              ? 'Sending a code…'
              : sentTo
                ? <>We sent a 4-digit code to <span className={`font-semibold ${theme.text}`}>{sentTo}</span>.</>
                : 'Enter the 4-digit code from your email.'}
          </p>

          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={e => { if (e.key === 'Enter') onVerify(); }}
            placeholder="••••"
            /* ⚠️ `ring-current`, not a colour. Tailwind's default ring is
               blue-500, so dropping `focus:ring-blue-500` still left a blue
               halo on the one focused control inside an orange dialog. There is
               no ring token on the themes, and adding one would mean editing
               all ten; `current` takes the ring from the input's own themed
               text colour, so it follows whatever theme is in scope. */
            className={`w-full px-3 py-3 border rounded-lg text-center text-2xl tracking-[0.5em] ${theme.input} focus:ring-2 focus:ring-current`}
          />

          <button
            onClick={onResend}
            disabled={sending}
            className={`text-xs ${theme.textMuted} underline disabled:opacity-50`}
          >
            {sending ? 'Sending…' : 'Send a new code'}
          </button>

          <div className={`flex gap-3 pt-2 border-t ${theme.border}`}>
            <button
              onClick={onClose}
              className={`flex-1 px-4 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg`}
            >
              Cancel
            </button>
            <button
              onClick={onVerify}
              disabled={verifying || code.length < 4}
              className={`flex-1 px-4 py-2 ${theme.buttonPrimary} rounded-lg disabled:opacity-50`}
            >
              {verifying ? 'Verifying…' : 'Verify'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepUpGate;
